import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { fetchCatalog } from "@/lib/api";
import { describeExpert, useTitle } from "@/lib/brand";
import { ExpertCard, Loading, PageHead } from "@/components/Kit";

/** THE EXPERTS — R2's catalog.json (L39), every Expert free, by Engine. Rebuilt 2026-10-27. */
export default function CatalogPage() {
  useTitle("Experts");
  const [sp, setSp] = useSearchParams();
  const filter = sp.get("engine");
  const [experts, setExperts] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    fetchCatalog().then((d) => setExperts((d.experts || []).filter((e) => e.available !== false))).catch(() => setExperts([]));
  }, []);

  const visible = useMemo(() => {
    let list = experts || [];
    if (filter) list = list.filter((e) => e.engine_id === filter);
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter((e) => [describeExpert(e).title, e.domain, e.persona].join(" ").toLowerCase().includes(needle));
    return list;
  }, [experts, filter, q]);

  const count = (id) => (experts || []).filter((e) => !id || e.engine_id === id).length;
  const setFilter = (v) => { const n = new URLSearchParams(sp); if (v) n.set("engine", v); else n.delete("engine"); setSp(n, { replace: true }); };
  const tabs = [[null, "All"], ["engine-7b", "7B Engine"], ["engine-4b", "4B Engine"]];

  return (
    <main data-testid="catalog-page" className="ex-page">
      <div className="ex-wrap">
        <PageHead
          eyebrow="Experts"
          title="Specialists for your Engine."
          lede="Every Expert here is free, tested on its Engine before it is published, and yours to keep once it's on your phone. Install from the app with Get an Expert, or download the file here."
        />
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between pb-8 border-b border-[color:var(--ex-line)]">
          <div className="inline-flex p-1 rounded-full bg-[color:var(--ex-raised)] border border-[color:var(--ex-line)] self-start">
            {tabs.map(([id, label]) => (
              <button key={label} type="button" onClick={() => setFilter(id)}
                className={`px-4 h-9 rounded-full text-[14px] transition-colors ${filter === id || (!filter && !id) ? "bg-white text-[color:var(--ex-bg)]" : "text-[color:var(--ex-secondary)] hover:text-white"}`}>
                {label} <span className="opacity-60 ml-1">{experts ? count(id) : ""}</span>
              </button>
            ))}
          </div>
          <label className="relative md:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--ex-faint)]" strokeWidth={1.8} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Experts" className="ex-text-input pl-11 rounded-full" data-testid="catalog-search" />
          </label>
        </div>

        <div className="py-10 pb-28">
          {experts === null ? <Loading label="Loading Experts" /> : visible.length === 0 ? (
            <div className="ex-panel p-10 text-center">
              <div className="text-white">No Experts match that yet.</div>
              <div className="ex-small mt-2">New Experts are added as they pass their tests. You can also train your own.</div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((e) => <ExpertCard key={e.id} expert={e} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
